import { isPlatformServer } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { debounce } from '../../lib/debounce';
import { throttle } from '../../lib/throttle';
import { InViewService } from '../../services/in-view.service';
import { ScrollService } from '../../services/scroll.service';

@Directive({
  selector: '[inView]',
  standalone: true,
})
export class InViewDirective implements OnInit, OnDestroy, AfterViewInit {
  private renderer = inject(Renderer2);
  private templateRef = inject<TemplateRef<unknown>>(TemplateRef<unknown>);
  private viewContainer = inject(ViewContainerRef);
  private element = inject(ElementRef);
  private platformId = inject<string>(PLATFORM_ID);
  private scrollService = inject(ScrollService);
  private zone = inject(NgZone);
  private check = inject(ChangeDetectorRef);
  private inViewService = inject(InViewService);
  // listenFunc will hold the function returned by "renderer.listen"
  listenFunc: () => void;
  @Input() inView: string | boolean;
  @Input() inViewKeep: boolean;
  @Input() inViewBuffer: number = 20;
  destroyed$ = new Subject<void>();

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) return;
    this.zone.runOutsideAngular(() => {
      this.renderer.listen('window', 'resize', () => this.displayElementThrottled());
      this.scrollService.onScroll.pipe(takeUntil(this.destroyed$)).subscribe(() => this.displayElementThrottled());
      this.inViewService.reCalculate$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.displayElementDebounced());
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.displayElement();
    });
  }

  displayElement() {
    if (this.isVisible()) this.doDisplayElement();
    else if (!this.inViewKeep) this.viewContainer.clear();
  }

  displayElementThrottled = throttle(() => this.displayElement(), 50);
  displayElementDebounced = debounce(() => this.displayElement(), 50);

  isVisible() {
    if (this.inView) return true; // override
    const el = <HTMLElement>this.element.nativeElement.parentElement;
    const bounds = el.getBoundingClientRect();

    if (!bounds.height && !bounds.width && !bounds.x && !bounds.y) return false;
    const top = bounds.top;
    const bottom = bounds.bottom;

    if (bottom < -this.inViewBuffer) return false;
    if (top > window.innerHeight + this.inViewBuffer) return false;
    return true;
  }

  private doDisplayElement() {
    if (!this.viewContainer.get(0)) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.check.detectChanges();
    }
  }

  shouldShow() {
    return;
  }

  ngOnDestroy() {
    this.listenFunc?.();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
